import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Search, Loader2, MessageSquare, 
  TrendingUp, Filter, MoreVertical, ChevronRight, Info 
} from 'lucide-react';
import { salesService } from '../../services/salesService';
import StatusBadge from '../../components/StatusBadge';

const LEAD_STATUSES = ['New', 'In Discussion', 'Meeting Scheduled', 'Negotiation', 'Won', 'Lost'];

const Leads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  const loadLeads = async () => {
    setLoading(true);
    try {
      const res = await salesService.getLeads(statusFilter);
      setLeads(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLeads(); }, [statusFilter]);

  return (
    <div className="space-y-6 pb-10">
      {/* Header */}
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Sales Pipeline</h1>
          <p className="text-sm text-gray-500 font-medium">Track and manage potential client leads</p>
        </div>
        <button 
          onClick={() => navigate('/sales/new')} // Naye route par jayenge
          className="bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-primary/20 hover:bg-primary-dark transition-all flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add New Lead
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-2">
        <button 
          onClick={() => setStatusFilter('')}
          className={'px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap ' + (statusFilter === '' ? 'bg-primary text-white shadow-md' : 'bg-white text-gray-500 border border-gray-100')}
        >
          All Leads
        </button>
        {LEAD_STATUSES.map(s => (
          <button 
            key={s}
            onClick={() => setStatusFilter(s)}
            className={'px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap ' + (statusFilter === s ? 'bg-primary text-white shadow-md' : 'bg-white text-gray-500 border border-gray-100')}
          >
            {s}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary opacity-20 w-10 h-10" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {leads.length === 0 ? (
            <div className="bg-white p-12 text-center rounded-card border border-dashed border-gray-200">
              <Info className="w-10 h-10 text-gray-200 mx-auto mb-2" />
              <p className="text-gray-400 font-medium">No leads found in this stage.</p>
            </div>
          ) : (
            leads.map((lead) => (
              <div 
                key={lead.id}
                onClick={() => navigate('/sales/leads/' + lead.lead_id)}
                className="bg-white p-5 rounded-card border border-gray-100 shadow-soft flex items-center justify-between gap-4 hover:border-primary/30 cursor-pointer transition-all group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center text-primary group-hover:bg-primary/5">
                    <TrendingUp className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 group-hover:text-primary transition-colors">{lead.client_name}</h3>
                    <p className="text-xs text-gray-500 font-medium">{lead.service_required} &bull; {lead.platform}</p>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="text-right hidden sm:block">
                    <p className="text-[10px] uppercase font-bold text-gray-400">Value</p>
                    <p className="text-sm font-black text-gray-900">{lead.estimated_value.toLocaleString()} {lead.currency}</p>
                  </div>
                  <StatusBadge status={String(lead.status)} />
                  <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-primary" />
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default Leads;